import { CircleCheck, CircleDollarSign, ExternalLink } from 'lucide-react';
import type { wave } from '@round/api';
import type { OfferPost } from '@/utility/offerPosts';
import { PLATFORM_ICON, PLATFORM_LABEL } from '@/utility/campaignDisplay';
import { formatMoney } from '@/utility/money';
import AccountAvatar from '@/creator/components/AccountAvatar';
import PlatformIcon from '@/creator/components/PlatformIcon';
import StatusPill from '@/creator/components/StatusPill';

const formatRefreshedAt = (value: string | null): string => {
    if (!value) {
        return '';
    }
    const refreshedMs = new Date(value).getTime();
    if (Number.isNaN(refreshedMs)) {
        return '';
    }
    return new Date(refreshedMs).toLocaleString(undefined, {
        day: 'numeric',
        month: 'short',
        hour: 'numeric',
        minute: '2-digit',
    });
};

const getAccountPostCount = (postCount: number): string => `${postCount} ${postCount === 1 ? 'post' : 'posts'}`;

const getAccountStatus = (posts: OfferPost[]): { label: string; className: string } => {
    if (posts.some((post) => post.reviewStatus === 'pending_review')) {
        return { label: 'In review', className: 'is-review' };
    }
    // A rejected post that's since been replaced leaves nothing to change; the post itself still says it was rejected.
    if (posts.every((post) => post.reviewStatus === 'rejected')) {
        return { label: 'Changes needed', className: 'is-rejected' };
    }
    return { label: 'Approved', className: 'is-approved' };
};

type AccountPostGroup = {
    key: string;
    handle: string;
    platform: wave.InvitePlatform;
    avatarUrl: string | null;
    posts: OfferPost[];
};

// Posts arrive in backend order; a group takes the position of its first post.
const groupByAccount = (posts: OfferPost[]): AccountPostGroup[] => {
    const groups = new Map<string, AccountPostGroup>();
    posts.forEach((post) => {
        const key = post.handle ?? 'unattributed';
        const group = groups.get(key);
        if (group) {
            group.posts.push(post);
            return;
        }
        groups.set(key, {
            key,
            handle: post.handle ?? 'Account to be confirmed',
            platform: post.platform,
            avatarUrl: post.avatarUrl,
            posts: [post],
        });
    });
    return [...groups.values()];
};

type AccountPostGroupListProps = {
    groups: AccountPostGroup[];
    isCpm: boolean;
};

const AccountPostGroupList = ({ groups, isCpm }: AccountPostGroupListProps) => (
    <>
        {groups.map((group) => {
            const accountStatus = getAccountStatus(group.posts);
            return (
                <article className="campaign-account-activity" key={group.key}>
                    <div className="campaign-account-activity__header">
                        <span
                            className={`campaign-account-activity__platform campaign-account-activity__platform--${PLATFORM_ICON[
                                group.platform
                            ].toLowerCase()}`}
                            aria-label={PLATFORM_LABEL[group.platform]}
                        >
                            <PlatformIcon platform={PLATFORM_ICON[group.platform]} size={13} />
                        </span>
                        <AccountAvatar src={group.avatarUrl} alt={group.handle} />
                        <b className="campaign-account-activity__handle">{group.handle}</b>
                        <span className="campaign-account-activity__meta">
                            <strong>{getAccountPostCount(group.posts.length)}</strong>
                            <small className={accountStatus.className}>{accountStatus.label}</small>
                        </span>
                    </div>
                    <div className="campaign-account-activity__posts">
                        {group.posts.map((post, index) => {
                            const { metrics } = post;
                            const updatedAt = formatRefreshedAt(
                                (isCpm ? metrics?.cpmUpdatedAt : metrics?.refreshedAt) ?? null
                            );
                            const cpmEarningsViews = metrics?.cpmViews ?? null;
                            const cpmEarningsAmount = metrics?.cpmEarned ?? null;
                            const hasCpmSnapshot = cpmEarningsViews !== null && cpmEarningsAmount !== null;
                            const reviewReason = post.reviewStatus === 'rejected' ? post.reviewReason : '';
                            const reasonId = `post-${post.id}-reason`;
                            return (
                                <a
                                    className="campaign-account-post campaign-account-post--link"
                                    href={post.url}
                                    target="_blank"
                                    rel="noreferrer"
                                    aria-label={`Open post ${index + 1} from ${group.handle}`}
                                    aria-describedby={reviewReason ? reasonId : undefined}
                                    key={post.id}
                                >
                                    <span className="campaign-account-post__identity">
                                        <b>
                                            <span>{`Post ${index + 1}`}</span>
                                            <ExternalLink size={11} />
                                        </b>
                                        <StatusPill
                                            status={
                                                post.reviewStatus === 'pending_review' ? 'review' : post.reviewStatus
                                            }
                                        />
                                    </span>
                                    <span className="campaign-account-post__result">
                                        {post.reviewStatus === 'pending_review' ? (
                                            <strong>Awaiting review</strong>
                                        ) : post.reviewStatus === 'rejected' ? (
                                            <strong>Changes needed</strong>
                                        ) : metrics === null ? null : isCpm ? (
                                            hasCpmSnapshot ? (
                                                <>
                                                    <strong>
                                                        {cpmEarningsViews.toLocaleString('en-GB')} eligible views
                                                    </strong>
                                                    <strong>
                                                        {formatMoney(metrics.currencySymbol, cpmEarningsAmount)} earned
                                                    </strong>
                                                </>
                                            ) : (
                                                <strong>Earnings pending</strong>
                                            )
                                        ) : (
                                            <strong>
                                                {metrics.viewCount === null
                                                    ? 'Views pending'
                                                    : `${metrics.viewCount.toLocaleString('en-GB')} views`}
                                            </strong>
                                        )}
                                        {updatedAt ? <small>Last updated at {updatedAt}</small> : null}
                                    </span>
                                    {reviewReason ? (
                                        <span className="campaign-account-post__reason" id={reasonId}>
                                            {reviewReason}
                                        </span>
                                    ) : null}
                                </a>
                            );
                        })}
                    </div>
                </article>
            );
        })}
    </>
);

type Props = {
    contract: wave.CreatorContract;
    posts: OfferPost[];
};

/** An offer's posts, grouped by the account that made them, with a CPM offer's earnings against its ceiling when known. */
const OfferPosts = ({ contract, posts }: Props) => {
    const isCpm = contract.price_type === 'cpm';
    const shouldShowEarnings = isCpm && posts.some((post) => post.metrics !== null);
    const offerViews = posts.reduce((total, post) => total + (post.metrics?.cpmViews ?? 0), 0);
    const offerEarned = posts.reduce((total, post) => total + Number(post.metrics?.cpmEarned ?? 0), 0);
    const maxEarnable = contract.max_earnable === null ? null : Number(contract.max_earnable);
    const earnedPercent =
        maxEarnable === null || maxEarnable <= 0 ? 0 : Math.min(100, Math.round((offerEarned / maxEarnable) * 100));
    const symbol = contract.display_currency.symbol;
    return (
        <div className="contract-card__posts">
            {shouldShowEarnings ? (
                <div className="campaign-performance__metrics">
                    <div>
                        <span className="campaign-performance__icon">
                            <CircleCheck size={16} />
                        </span>
                        <span>Approved views</span>
                        <strong>{offerViews.toLocaleString('en-GB')}</strong>
                    </div>
                    <div>
                        <span className="campaign-performance__icon">
                            <CircleDollarSign size={16} />
                        </span>
                        <span>Earned so far</span>
                        <strong>{formatMoney(symbol, offerEarned)}</strong>
                    </div>
                </div>
            ) : null}
            {shouldShowEarnings && maxEarnable !== null ? (
                <div>
                    <div
                        className="campaign-performance__bar"
                        role="progressbar"
                        aria-valuenow={earnedPercent}
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-label="Earnings against this offer's maximum payout"
                    >
                        <span style={{ width: `${earnedPercent}%` }} />
                    </div>
                    <div className="campaign-performance__footer">
                        <span>{`${earnedPercent}% of ${formatMoney(symbol, maxEarnable)} max payout`}</span>
                        <span>{`${formatMoney(symbol, Math.max(0, maxEarnable - offerEarned))} remaining`}</span>
                    </div>
                </div>
            ) : null}
            <AccountPostGroupList groups={groupByAccount(posts)} isCpm={isCpm} />
        </div>
    );
};

export default OfferPosts;
